import { MessageResponseDto } from "@api/api";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { cardSeriesFormDefault } from "@factory/form/form.default";
import {
  CardSeriesFormRule,
  CardSeriesFormSchema,
} from "@factory/form/form.rule";
import { useQyGenerateCardSeries } from "@factory/query/card-series.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

export function useNewCardSeries() {
  // LOCAL DECLARATION
  const { userRoleIsAdmin } = useUserIdentityContext();
  const navigate = useNavigate();
  const { mapCreateCardSeries } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    showSuccess(`New CardSeries is created`);
    navigate(`../`);
  };
  const { mutate: addCardSeries, isLoading: isCreating } =
    useQyGenerateCardSeries(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<CardSeriesFormSchema>({
    defaultValues: cardSeriesFormDefault,
    resolver: zodResolver(CardSeriesFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: CardSeriesFormSchema) => {
    if (!userRoleIsAdmin()) {
      showError("Please ask sales / admin for card generation");
      return;
    }
    const formData = mapCreateCardSeries(form);
    addCardSeries(formData);
  };
  const handleValidateError = (err: FieldErrors<CardSeriesFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
